import classNames from 'classnames';
import { Token } from '@/pages/race-wizard';
import { TokenData } from '@/stores/TokenOptionsStore';
import { formatFullyDilutedValuation } from '@/utils/tokens';

export const TokensTable = ({
  tokens,
  isLoading,
  selectedTokens,
  onTokenSelect,
}: {
  tokens: TokenData[];
  isLoading: boolean;
  selectedTokens: Token[];
  onTokenSelect: (token: Token) => void;
}) => {
  const showLoader = isLoading && tokens.length === 0;
  const showEmpty = !isLoading && tokens.length === 0;

  if (showLoader) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-[3px] border-[#333] border-t-[#ff3b30]" />
      </div>
    );
  }

  if (showEmpty) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="mx-auto h-12 w-12 text-gray-400">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="mt-2 text-gray-500">No data found</p>
        <p className="text-sm text-gray-400">Try adjusting your search or categories</p>
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead className="sticky top-0 z-30 bg-[#10091C] text-[#8E8E93]">
        <tr className="text-end text-xs *:font-normal">
          <th className="min-w-10 text-start">Add</th>
          <th className="min-w-10 text-start">#</th>
          <th className="min-w-48 text-start">Name</th>
          <th className="min-w-24 py-2">Price</th>
          <th className="min-w-24">1h %</th>
          <th className="min-w-24">24h %</th>
          <th className="min-w-24">FDV</th>
        </tr>
      </thead>
      <tbody>
        {tokens.map((token, index) => {
          return (
            <TokenTableRow
              index={index}
              key={token.id}
              token={token}
              onTokenSelect={onTokenSelect}
              selectedTokens={selectedTokens}
            />
          );
        })}
      </tbody>
    </table>
  );
};

const TokenTableRow = ({
  token,
  index,
  onTokenSelect,
  selectedTokens,
}: {
  token: TokenData;
  index: number;
  selectedTokens: Token[];
  onTokenSelect: (token: Token) => void;
}) => {
  const isSelected = selectedTokens.some(t => t.id === token.id);

  return (
    <tr onClick={() => onTokenSelect(token)} key={token.id} className="text-end">
      <td className="text-start">
        <input type="checkbox" checked={isSelected} readOnly />
      </td>
      <td className="text-start">{index + 1}</td>
      <td className="flex items-center gap-2 py-2 text-start">
        <img src={token.image} alt={token.id} className="size-5 rounded-full" />
        <span>{token.name}</span>
        <span className="text-[10px] text-white/40">{token.symbol}</span>
      </td>
      <td>{formatFullyDilutedValuation(token?.price)}</td>
      <td
        className={classNames({
          'text-[#10B981]': token.change1h > 0,
          'text-[#EF4444]': token.change1h < 0,
        })}
      >
        {token.change1h}%
      </td>
      <td
        className={classNames({
          'text-[#10B981]': token.change24h > 0,
          'text-[#EF4444]': token.change24h < 0,
        })}
      >
        {token.change24h}%
      </td>
      <td>{formatFullyDilutedValuation(token.fdv, 'compact')}</td>
    </tr>
  );
};
